import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Card, CardApplicationValidation } from 'src/front/api/apiInstance';
import { CardSelectionType, ReferenceDataType } from 'src/front/api/generated';

import infoIcon from 'src/front/assets/icon_info.svg';
import usePRM from 'src/front/common/hooks/usePRM';
import {
  dictionaryLineFormat,
  formatEmbossName,
  formatFreeText,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { CardApplicationDetailSchema } from 'src/front/common/utils/validationSchemas';

import {
  CheckboxContainer,
  Container,
  FormElement,
  FormRow,
  LineSeparator,
  Separator,
  Subtitle,
  Title,
  WarningContainer,
  WarningIcon,
} from 'src/front/components/FormStyles';

import {
  useCardTypeState,
  useCustomerSearchState,
  useProductOfferDebitState,
  useSwitchCustomerType,
} from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';

const InfoLabel = styled.div`
  display: inline-block;
  width: 150px;
`;

interface FormData {
  customerAccount: Option | null;
  openNewAccount: boolean;
  cardEmbossedName: string;
  isNoNameCard: boolean;
  cardNumber: string;
  lastFourDigits: string;
  promotionName: Option | null;
  promotionDaysNumber: string;
  salaryCompanyCode: string;
  deliveryChannelCode: string;
  // TODO (ozgunbal): Can be added later
  // isMarketingViaEmail: boolean;
  hasNoAccount: boolean;
  branch: Option | null;
  metroCardNumber: string;
  consentBik: boolean;
  agreementToMarketing: boolean;
}

const defaultValues = {
  customerAccount: null,
  openNewAccount: false,
  cardEmbossedName: '',
  isNoNameCard: false,
  cardNumber: '',
  lastFourDigits: '',
  promotionName: null,
  promotionDaysNumber: '',
  salaryCompanyCode: '',
  deliveryChannelCode: '',
  // isMarketingViaEmail: false,
  hasNoAccount: false,
  branch: null,
  metroCardNumber: '',
  consentBik: true,
  agreementToMarketing: true,
};

interface Props {
  goToNextStep: (customerId?: string) => void;
  customerId?: string;
  isEdit?: boolean;
}

const CardApplicationDetail: FC<Props> = ({ customerId, goToNextStep, isEdit = false }) => {
  const { t } = useTranslation();

  const switchCustomerTypeDispatch = useSwitchCustomerType();
  const isProductOfferDebit = useProductOfferDebitState();
  const isDebitCard = useCardTypeState() === CardSelectionType.DEBIT || isProductOfferDebit;
  const isCreditCard = useCardTypeState() === CardSelectionType.CREDITCARD || !isProductOfferDebit;
  const { isNewCustomer } = useCustomerSearchState();

  const [nameLength, setNameLength] = useState<number>(21);
  const [currentAccounts, setCurrentAccounts] = useState<Option[]>([]);
  const [promotionNames, setPromotionNames] = useState<Option[]>([]);
  const [customerInfo, setCustomerInfo] = useState<{
    customerNumber: string;
    customerName: string;
  }>();
  const [isDeliveryChannelCodeVisible, setIsDeliveryChannelCodeVisible] = useState<boolean>();
  const [isSalaryCompanyCodeVisible, setIsSalaryCompanyCodeVisible] = useState<boolean>();
  const [metroCardNumberLength, setMetroCardNumberLength] = useState<number>(0);
  const [isNoNameCardRequired, setIsNoNameCardRequired] = useState<boolean>();
  const [isBranchEnable, setIsBranchEnable] = useState<boolean>(true);
  const [isCardPlus, setIsCardPlus] = useState<boolean | undefined>(false);
  const [isConsentBik, setIsConsentBik] = useState<boolean | undefined>(false);

  const [isOpenNewAccountLabelVisible, setIsOpenNewAccountLabelVisible] = useState<boolean>(false);
  const [isOpenNewAccountCheckboxVisible, setIsOpenNewAccountCheckboxVisible] = useState<boolean>(
    false
  );
  const [
    willOpenNewAccountCheckboxCheckedAndDisabled,
    setWillOpenNewAccountCheckboxCheckedAndDisabled,
  ] = useState<boolean>(false);
  const [isCurrentAccountComboVisible, setIsCurrentAccountComboVisible] = useState<boolean>(false);
  const [disabledAgreementToMarketing, setDisabledAgreementToMarketing] = useState<boolean>(false);

  const branches = usePRM(ReferenceDataType.APPLICATIONBRANCH);

  const { handleSubmit, errors, register, watch, setValue, control, formState } = useForm<FormData>(
    {
      defaultValues: {
        ...defaultValues,
        openNewAccount: isNewCustomer || isProductOfferDebit,
      },
      resolver: yupResolver(CardApplicationDetailSchema, {
        abortEarly: false,
        context: { isCreditCard, metroCardNumberLength, isCardPlus },
      }),
    }
  );

  useEffect(() => {
    switchCustomerTypeDispatch({
      isNewCustomer: false,
      customerNumber: customerInfo?.customerNumber,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      const [cardInfoResponse, customerInfoResponse, sessionResponse] = await Promise.all([
        Card.getEmbossedCardInfo(),
        Card.getCardCustomerInfo(),
        CardApplicationValidation.getApplicationInfoPageData(),
      ]);

      if (customerInfoResponse.data.result === 'OK') {
        setCustomerInfo({
          customerName: customerInfoResponse.data.customerName,
          customerNumber: customerInfoResponse.data.customerNumber,
        });
      }

      if (cardInfoResponse.data.result === 'OK') {
        setDisabledAgreementToMarketing(cardInfoResponse.data.agreementToMarketing || false);
      }

      if (
        sessionResponse.data.result === 'OK' &&
        cardInfoResponse.data.result === 'OK' &&
        sessionResponse.data.pageData
      ) {
        const {
          isNewAccount,
          customerAccount,
          isNoNameCard,
          cardEmbossedName,
          cardNumber,
          lastFourDigits,
          promotionName,
          promotionDaysNumber,
          deliveryChannelCode,
          salaryCompanyCode,
          branch: applicationBranch,
          metroCardNumber,
          consentBik,
          // isMarketingViaEmail,
          agreementToMarketing,
        } = sessionResponse.data.pageData;

        [
          { name: 'isNoNameCard', value: cardInfoResponse.data.isCardTypeNoName || isNoNameCard },
          { name: 'cardEmbossedName', value: cardEmbossedName || '' },
          { name: 'cardNumber', value: cardNumber || '' },
          { name: 'lastFourDigits', value: lastFourDigits || '' },
          ...(isDebitCard
            ? [
                { name: 'openNewAccount', value: isNewAccount },
                {
                  name: 'customerAccount',
                  value: customerAccount ? optionFormat(customerAccount) : null,
                },
                { name: 'promotionDaysNumber', value: promotionDaysNumber || '' },
                {
                  name: 'promotionName',
                  value: promotionName ? optionFormat(promotionName) : null,
                },
              ]
            : []),
          { name: 'deliveryChannelCode', value: deliveryChannelCode || '' },
          { name: 'salaryCompanyCode', value: salaryCompanyCode || '' },
          { name: 'branch', value: applicationBranch ? optionFormat(applicationBranch) : null },
          { name: 'metroCardNumber', value: metroCardNumber || '' },
          { name: 'consentBik', value: consentBik || '' },
          // { name: 'isMarketingViaEmail', value: isMarketingViaEmail },
          { name: 'agreementToMarketing', value: agreementToMarketing || true },
        ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

        setMetroCardNumberLength(cardInfoResponse.data.metroCardNumberMaxLength || 0);
        setIsDeliveryChannelCodeVisible(cardInfoResponse.data.isDeliveryChannelCodeVisible);
        setIsSalaryCompanyCodeVisible(cardInfoResponse.data.isSalaryCompanyCodeVisible);
        setIsNoNameCardRequired(cardInfoResponse.data.isCardTypeNoName);
        setIsBranchEnable(!!cardInfoResponse.data.isBranchEnable);
        setIsCardPlus(cardInfoResponse.data.isCardPlus);
        setIsConsentBik(cardInfoResponse.data.isConsentBik);

        setIsOpenNewAccountLabelVisible(
          cardInfoResponse.data.isOpenNewAccountLabelVisible || false
        );
        setIsCurrentAccountComboVisible(
          cardInfoResponse.data.isCurrentAccountComboVisible || false
        );
        setIsOpenNewAccountCheckboxVisible(
          cardInfoResponse.data.isOpenNewAccountCheckboxVisible || false
        );
        setWillOpenNewAccountCheckboxCheckedAndDisabled(
          cardInfoResponse.data.willOpenNewAccountCheckboxCheckedAndDisabled || false
        );
        setCurrentAccounts(cardInfoResponse.data.currentAccountList?.map(optionFormat) || []);

        setPromotionNames(promotionName ? [optionFormat(promotionName)] : []);

        return;
      }

      if (cardInfoResponse.data.result === 'OK') {
        const applicationBranch = branches.find(
          (branch) => branch.value === cardInfoResponse.data.branchCode
        );

        [
          { name: 'cardEmbossedName', value: cardInfoResponse.data.cardEmbossedName },
          ...(isDebitCard && cardInfoResponse.data.isCardPlus
            ? [
                {
                  name: 'promotionDaysNumber',
                  value: cardInfoResponse.data.promotionDaysNumber || '',
                },
                {
                  name: 'promotionName',
                  value: cardInfoResponse.data.promotionName
                    ? optionFormat(cardInfoResponse.data.promotionName)
                    : null,
                },
              ]
            : []),
          { name: 'isNoNameCard', value: cardInfoResponse.data.isCardTypeNoName },
          { name: 'branch', value: applicationBranch },
          {
            name: 'openNewAccount',
            value: cardInfoResponse.data.willOpenNewAccountCheckboxCheckedAndDisabled,
          },
        ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

        setNameLength(Number(cardInfoResponse.data.cardEmbossedNameMaxLength));
        setMetroCardNumberLength(cardInfoResponse.data.metroCardNumberMaxLength || 0);
        setIsDeliveryChannelCodeVisible(cardInfoResponse.data.isDeliveryChannelCodeVisible);
        setIsSalaryCompanyCodeVisible(cardInfoResponse.data.isSalaryCompanyCodeVisible);
        setIsNoNameCardRequired(cardInfoResponse.data.isCardTypeNoName);
        setIsBranchEnable(!!cardInfoResponse.data.isBranchEnable);
        setIsCardPlus(cardInfoResponse.data.isCardPlus);
        setIsConsentBik(cardInfoResponse.data.isConsentBik);

        setIsOpenNewAccountLabelVisible(
          cardInfoResponse.data.isOpenNewAccountLabelVisible || false
        );
        setIsCurrentAccountComboVisible(
          cardInfoResponse.data.isCurrentAccountComboVisible || false
        );
        setIsOpenNewAccountCheckboxVisible(
          cardInfoResponse.data.isOpenNewAccountCheckboxVisible || false
        );
        setWillOpenNewAccountCheckboxCheckedAndDisabled(
          cardInfoResponse.data.willOpenNewAccountCheckboxCheckedAndDisabled || false
        );
        setValue(
          'openNewAccount',
          cardInfoResponse.data.willOpenNewAccountCheckboxCheckedAndDisabled || false
        );
        setCurrentAccounts(cardInfoResponse.data.currentAccountList?.map(optionFormat) || []);

        setPromotionNames(
          cardInfoResponse.data.promotionName
            ? [optionFormat(cardInfoResponse.data.promotionName)]
            : []
        );
      }
    };

    if (branches.length) {
      init();
    }
  }, [isDebitCard, branches, setValue]);

  useEffect(() => {
    register({ name: 'customerAccount' });
    register({ name: 'cardEmbossedName' });
    register({ name: 'isNoNameCard' });
    register({ name: 'openNewAccount' });
    register({ name: 'hasNoAccount' });
    register({ name: 'cardNumber' });
    register({ name: 'lastFourDigits' });
    register({ name: 'salaryCompanyCode' });
    register({ name: 'deliveryChannelCode' });
    register({ name: 'metroCardNumber' });
    register({ name: 'promotionName' });
    register({ name: 'promotionDaysNumber' });
    register({ name: 'consentBik' });
    register({ name: 'agreementToMarketing' });
  }, [register]);

  const handleTextChange = (fieldName: string) => (text: string) =>
    setValue(fieldName, text, {
      shouldValidate: formState.isSubmitted || !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  useEffect(() => {
    setValue('hasNoAccount', currentAccounts.length === 0);
  }, [currentAccounts, setValue]);

  const onSubmit = async (form: FormData): Promise<void> => {
    const { data } = await CardApplicationValidation.validateApplicationInfoPageData({
      ...(!form.openNewAccount
        ? { customerAccount: dictionaryLineFormat(form.customerAccount) }
        : {}),
      isNoNameCard: form.isNoNameCard,
      ...(form.isNoNameCard ? {} : { cardEmbossedName: form.cardEmbossedName }),
      ...(form.isNoNameCard
        ? {
            cardNumber: form.cardNumber,
            lastFourDigits: form.lastFourDigits,
          }
        : {}),
      ...(isDebitCard
        ? {
            isNewAccount: form.openNewAccount,
            promotionName: dictionaryLineFormat(form.promotionName),
            promotionDaysNumber: form.promotionDaysNumber,
            agreementToMarketing: form.agreementToMarketing,
          }
        : {}),
      ...(form.deliveryChannelCode ? { deliveryChannelCode: form.deliveryChannelCode } : {}),
      ...(form.salaryCompanyCode ? { salaryCompanyCode: form.salaryCompanyCode } : {}),
      branch: dictionaryLineFormat(form.branch),
      ...(metroCardNumberLength > 0 ? { metroCardNumber: form.metroCardNumber } : {}),
      // isMarketingViaEmail: form.isMarketingViaEmail,
      ...(isConsentBik ? { consentBik: form.consentBik } : {}),
    });

    if (data.result === 'OK') {
      goToNextStep(customerId);
    }
  };

  const handleAccountChange = (option: Option | null): void => {
    setValue('customerAccount', option, { shouldValidate: true, shouldDirty: true });
  };

  const handleOpenNewAccountChange = async ({
    target,
  }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    setValue('openNewAccount', target.checked);
  };
  /* eslint-disable no-nested-ternary */
  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_cardInfo_appDetail_title" />
      </Title>
      {customerInfo ? (
        <>
          <FormRow>
            <LineSeparator />
          </FormRow>

          <div>
            <InfoLabel>
              <Trans i18nKey="cardEntry_customerTable_name_title" />:
            </InfoLabel>
            {customerInfo.customerName}
          </div>
          <FormRow>
            <LineSeparator />
          </FormRow>
        </>
      ) : null}
      <form onSubmit={handleSubmit(onSubmit)}>
        {isDebitCard ? (
          <FormRow>
            <Checkbox
              disabled={disabledAgreementToMarketing}
              id="agreementToMarketing"
              label={t('cardEntry_cardInfo_appDetail_agreementToMarketing_label')}
              onChange={({ target }) => setValue('agreementToMarketing', target.checked)}
              value={watch('agreementToMarketing')}
            />
          </FormRow>
        ) : null}
        {isOpenNewAccountLabelVisible ? (
          <WarningContainer>
            <WarningIcon src={infoIcon} />
            <Subtitle>
              <Trans i18nKey="cardEntry_cardInfo_appDetail_newAccountInfo_text" />
            </Subtitle>
          </WarningContainer>
        ) : null}
        {isCurrentAccountComboVisible ? (
          <Subtitle>
            <Trans i18nKey="cardEntry_cardInfo_appDetail_accountLink_text" />
          </Subtitle>
        ) : null}
        <FormRow>
          {isCurrentAccountComboVisible ? (
            watch('openNewAccount') ? null : (
              <FormElement>
                <Combobox
                  errorMessage={t(errors?.customerAccount?.message as string)}
                  items={currentAccounts}
                  label={t('cardEntry_cardInfo_appDetail_alreadyOpenedAccounts_label')}
                  name="customerAccount"
                  onChange={handleAccountChange}
                  required={!isCardPlus}
                  value={watch('customerAccount')}
                />
              </FormElement>
            )
          ) : null}
          {isOpenNewAccountCheckboxVisible || willOpenNewAccountCheckboxCheckedAndDisabled ? (
            <CheckboxContainer>
              <Checkbox
                disabled={willOpenNewAccountCheckboxCheckedAndDisabled}
                id="openNewAccount"
                label={t('cardEntry_cardInfo_appDetail_newAccountOpened_label')}
                onChange={handleOpenNewAccountChange}
                value={watch('openNewAccount')}
              />
            </CheckboxContainer>
          ) : null}
        </FormRow>

        <Separator />
        {isConsentBik ? (
          <CheckboxContainer>
            <Checkbox
              disabled={!isConsentBik}
              id="consentBik"
              label={t('cardEntry_cardInfo_appDetail_consentBik_label')}
              onChange={({ target }) => setValue('consentBik', target.checked)}
              value={watch('consentBik')}
            />
          </CheckboxContainer>
        ) : null}
        <Subtitle>
          <Trans i18nKey="cardEntry_cardInfo_appDetail_subtitle" />
        </Subtitle>
        <FormRow>
          {watch('isNoNameCard') ? null : (
            <FormElement>
              <TextInput
                errorMessage={t(errors?.cardEmbossedName?.message as string)}
                formatter={formatEmbossName}
                label={t('cardEntry_cardInfo_appDetail_embossedName_label')}
                maxLength={nameLength}
                name="cardEmbossedName"
                onChange={handleTextChange('cardEmbossedName')}
                value={watch('cardEmbossedName')}
                required
              />
            </FormElement>
          )}
          {!isEdit ? (
            <CheckboxContainer>
              <Checkbox
                disabled={isNoNameCardRequired}
                id="isNoNameCard"
                label={t('cardEntry_cardInfo_appDetail_noNameCard_label')}
                onChange={({ target }) => setValue('isNoNameCard', target.checked)}
                value={watch('isNoNameCard')}
              />
            </CheckboxContainer>
          ) : null}
        </FormRow>
        {watch('isNoNameCard') ? (
          <FormRow>
            <FormElement>
              <TextInput
                errorMessage={t(errors?.cardNumber?.message as string)}
                formatter={formatNumeric}
                label={t('cardEntry_cardInfo_appDetail_uniqueCardCode_label')}
                maxLength={10}
                name="cardNumber"
                onChange={handleTextChange('cardNumber')}
                value={watch('cardNumber')}
                required
              />
            </FormElement>
            <FormElement>
              <TextInput
                errorMessage={t(errors?.lastFourDigits?.message as string)}
                formatter={formatNumeric}
                label={t('cardEntry_cardInfo_appDetail_last4Digit_label')}
                maxLength={4}
                name="lastFourDigits"
                onChange={handleTextChange('lastFourDigits')}
                value={watch('lastFourDigits')}
                required
              />
            </FormElement>
          </FormRow>
        ) : null}
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="branch"
              render={({ onChange, value }) => (
                <Combobox
                  disabled={isEdit || !isBranchEnable}
                  errorMessage={t((errors?.branch as FieldError)?.message as string)}
                  items={branches}
                  label={t('cardEntry_cardInfo_appDetail_branch_label')}
                  name="branch"
                  onChange={onChange}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
        </FormRow>
        {isDebitCard && isCardPlus ? (
          <>
            <Separator />
            <Subtitle>
              <Trans i18nKey="cardEntry_cardInfo_appDetail_accruralPromotion_subtitle" />
            </Subtitle>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="promotionName"
                  render={({ onChange, value }) => (
                    <Combobox
                      errorMessage={t(errors?.promotionName?.message as string)}
                      items={promotionNames}
                      label={t('cardEntry_cardInfo_appDetail_promotionName_label')}
                      name="promotionName"
                      onChange={onChange}
                      value={value}
                      disabled
                    />
                  )}
                />
              </FormElement>
              <FormElement>
                <TextInput
                  errorMessage={t(errors?.promotionDaysNumber?.message as string)}
                  label={t('cardEntry_cardInfo_appDetail_term_label')}
                  name="promotionDaysNumber"
                  onChange={handleTextChange('promotionDaysNumber')}
                  value={watch('promotionDaysNumber')}
                  disabled
                />
              </FormElement>
            </FormRow>
          </>
        ) : null}
        {isSalaryCompanyCodeVisible || isDeliveryChannelCodeVisible ? (
          <>
            <Separator />
            <FormRow>
              {isSalaryCompanyCodeVisible ? (
                <FormElement>
                  <TextInput
                    formatter={formatNumeric}
                    label={t('cardEntry_cardInfo_appDetail_companyCode_label')}
                    maxLength={4}
                    name="salaryCompanyCode"
                    onChange={handleTextChange('salaryCompanyCode')}
                    value={watch('salaryCompanyCode')}
                  />
                </FormElement>
              ) : null}
              {isDeliveryChannelCodeVisible ? (
                <FormElement>
                  <TextInput
                    formatter={formatFreeText}
                    label={t('cardEntry_cardInfo_appDetail_deliveryChannelCode_label')}
                    maxLength={5}
                    name="deliveryChannelCode"
                    onChange={handleTextChange('deliveryChannelCode')}
                    value={watch('deliveryChannelCode')}
                  />
                </FormElement>
              ) : null}
            </FormRow>
          </>
        ) : null}
        {metroCardNumberLength > 0 ? (
          <>
            <Separator />
            <FormRow>
              <FormElement>
                <TextInput
                  errorMessage={t(errors?.metroCardNumber?.message as string)}
                  formatter={formatNumeric}
                  label={`${t('cardEntry_cardInfo_appDetail_metroCardNumber_label')}`}
                  maxLength={metroCardNumberLength}
                  name="metroCardNumber"
                  onChange={handleTextChange('metroCardNumber')}
                  value={watch('metroCardNumber')}
                  required
                />
              </FormElement>
            </FormRow>
          </>
        ) : null}
        <Separator />
        {/* <FormRow>
          <Checkbox
            id="isMarketingViaEmail"
            label={t('cardEntry_cardInfo_appDetail_acceptMarketingEmail_label')}
            onChange={({ target }) => setValue('isMarketingViaEmail', target.checked)}
            value={watch('isMarketingViaEmail')}
          />
        </FormRow> */}
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </form>
    </Container>
  );
};

export default CardApplicationDetail;
